/**
 * CustomAxesHelper.js
 *
 * Helper for visualizing the 3D axes (X, Y, Z) in a Three.js scene, with customizable appearance.
 * Supports classic colored lines or custom axis entities (lines or boxes with gradients/colors).
 *
 * Usage:
 *   import { CustomAxesHelper } from '../objects/CustomAxesHelper';
 *   const axesHelper = new CustomAxesHelper(5); // Classic style, size 5
 *   scene.add(axesHelper);
 *
 * Author: [Your Name]
 * Date: [Update as needed]
 */

import * as THREE from 'three';
/**
 * CustomAxesHelper
 *
 * Three.js Object3D subclass for visualizing axes in a scene.
 * Can be constructed with a classic size (number) or a custom array of axis entities.
 * Each entity can be a line or a box, with custom colors and widths.
 *
 * @class
 * @extends THREE.Object3D
 * @param {number|Array} entities - Size (number) for classic axes, or array of axis entity objects.
 *   Each entity: { axis: 'x'|'y'|'z', positive: true|false, start: number, end: number, width: number, startColor: Color|string|number, endColor: Color|string|number }
 */
export class CustomAxesHelper extends THREE.Object3D {
    constructor(entities) {
        super();
        this.type = 'CustomAxesHelper';
        // If called with a number, treat as classic size argument
        if (typeof entities === 'number') {
            const size = entities;
            // Classic axes: positive and negative for each axis, with default colors
            entities = [
                { axis: 'x', positive: true, start: 0, end: size, width: 0, startColor: 0xff0000, endColor: 0xffff00 },
                { axis: 'x', positive: false, start: 0, end: -size, width: 0, startColor: 0x990000, endColor: 0xff00ff },
                { axis: 'y', positive: true, start: 0, end: size, width: 0, startColor: 0x00ff00, endColor: 0x00ffff },
                { axis: 'y', positive: false, start: 0, end: -size, width: 0, startColor: 0x009900, endColor: 0x0000ff },
                { axis: 'z', positive: true, start: 0, end: size, width: 0, startColor: 0x0000ff, endColor: 0xffffff },
                { axis: 'z', positive: false, start: 0, end: -size, width: 0, startColor: 0x000099, endColor: 0x00ff00 },
            ];
        }
        // If not array, fallback to default axes
        if (!Array.isArray(entities)) {
            entities = [
                { axis: 'x', positive: true, start: 0, end: 1, width: 0, startColor: 0xffffff, endColor: 0x000000 },
                { axis: 'x', positive: false, start: 0, end: -1, width: 0, startColor: 0xffffff, endColor: 0x000000 },
                { axis: 'y', positive: true, start: 0, end: 1, width: 0, startColor: 0xffffff, endColor: 0x000000 },
                { axis: 'y', positive: false, start: 0, end: -1, width: 0, startColor: 0xffffff, endColor: 0x000000 },
                { axis: 'z', positive: true, start: 0, end: 1, width: 0, startColor: 0xffffff, endColor: 0x000000 },
                { axis: 'z', positive: false, start: 0, end: -1, width: 0, startColor: 0xffffff, endColor: 0x000000 },
            ];
        }
        // Build each axis entity (line or box)
        entities.forEach(entity => {
            // Only use startColor and endColor for each entity
            const { axis, start, end, width, startColor, endColor } = entity;
            // Direction vector for axis
            let dir = new THREE.Vector3(
                axis === 'x' ? 1 : 0,
                axis === 'y' ? 1 : 0,
                axis === 'z' ? 1 : 0
            );
            if (!entity.positive) dir.multiplyScalar(-1);
            // Compute start/end points
            const startPt = dir.clone().multiplyScalar(start);
            const endPt = dir.clone().multiplyScalar(end);
            const cStart = new THREE.Color(startColor);
            const cEnd = endColor !== undefined ? new THREE.Color(endColor) : cStart;
            if (width > 0) {
                // Draw box (axis as a rectangular prism)
                const boxLength = Math.abs(end - start);
                const boxGeom = new THREE.BoxGeometry(width, width, boxLength);
                // Set vertex colors for box ends (color all vertices at one end with startColor, other end with endColor)
                const colorAttr = [];
                for (let i = 0; i < boxGeom.attributes.position.count; i++) {
                    const v = boxGeom.attributes.position;
                    // Pick axis value for this vertex
                    let val = 0;
                    if (axis === 'x') val = v.getX(i);
                    else if (axis === 'y') val = v.getY(i);
                    else if (axis === 'z') val = v.getZ(i);
                    // If closer to start, use startColor, else endColor
                    if (Math.abs(val - start) < Math.abs(val - end)) {
                        colorAttr.push(cStart.r, cStart.g, cStart.b);
                    } else {
                        colorAttr.push(cEnd.r, cEnd.g, cEnd.b);
                    }
                }
                boxGeom.setAttribute('color', new THREE.Float32BufferAttribute(colorAttr, 3));
                const boxMat = new THREE.MeshBasicMaterial({ vertexColors: true });
                const box = new THREE.Mesh(boxGeom, boxMat);
                // Center the box between start and end
                box.position.copy(startPt).add(endPt).multiplyScalar(0.5);
                // Rotate box to align with axis
                if (axis === 'x') box.rotation.z = Math.PI / 2;
                if (axis === 'z') box.rotation.x = Math.PI / 2;
                this.add(box);
            } else {
                // Draw line with vertex colors
                const geom = new THREE.BufferGeometry().setFromPoints([startPt, endPt]);
                const colors = [cStart.r, cStart.g, cStart.b, cEnd.r, cEnd.g, cEnd.b];
                geom.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
                const mat = new THREE.LineBasicMaterial({
                    vertexColors: true,
                    opacity: 0.5,
                    transparent: true
                });
                const line = new THREE.Line(geom, mat);
                this.add(line);
            }
        });
    }

    /**
     * Dynamically update the start color of all axes (lines and boxes).
     * @param {THREE.Color|string|number} color
     */
    setStartColor(color) {
        const c = new THREE.Color(color);
        this.traverse(obj => {
            if (obj.isLine && obj.geometry && obj.geometry.attributes.color) {
                // Update start vertex color (first 3 floats)
                const attr = obj.geometry.attributes.color;
                attr.setXYZ(0, c.r, c.g, c.b);
                attr.needsUpdate = true;
            } else if (obj.isMesh && obj.geometry && obj.geometry.attributes.color) {
                // For boxes, update all vertices closer to start
                const v = obj.geometry.attributes.position;
                const colorAttr = obj.geometry.attributes.color;
                for (let i = 0; i < v.count; i++) {
                    // Heuristic: if this vertex color matches the old start color, update it
                    // (Assumes startColor and endColor are distinct)
                    // For robust update, just update half of the vertices
                    if (i < v.count / 2) {
                        colorAttr.setXYZ(i, c.r, c.g, c.b);
                    }
                }
                colorAttr.needsUpdate = true;
            }
        });
    }

    /**
     * Dynamically update the end color of all axes (lines and boxes).
     * @param {THREE.Color|string|number} color
     */
    setEndColor(color) {
        const c = new THREE.Color(color);
        this.traverse(obj => {
            if (obj.isLine && obj.geometry && obj.geometry.attributes.color) {
                // Update end vertex color (last 3 floats)
                const attr = obj.geometry.attributes.color;
                attr.setXYZ(1, c.r, c.g, c.b);
                attr.needsUpdate = true;
            } else if (obj.isMesh && obj.geometry && obj.geometry.attributes.color) {
                // For boxes, update all vertices closer to end
                const v = obj.geometry.attributes.position;
                const colorAttr = obj.geometry.attributes.color;
                for (let i = 0; i < v.count; i++) {
                    // Heuristic: update the other half of the vertices
                    if (i >= v.count / 2) {
                        colorAttr.setXYZ(i, c.r, c.g, c.b);
                    }
                }
                colorAttr.needsUpdate = true;
            }
        });
    }

    /**
     * Dispose geometries and materials for all children (to avoid memory leaks).
     */
    dispose() {
        this.traverse(obj => {
            if (obj.geometry) obj.geometry.dispose();
            if (obj.material) obj.material.dispose();
        });
    }
}
